"use client";

import { useTranslations } from "@frontend/lib/next-intl-typed";
import { KioskMedriteCheckinRestartDialogKeys } from "@frontend/lib/translation-types";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@frontend/shared/components/ui/popover";
import { LogOut, Settings } from "lucide-react";
import { useLocale } from "next-intl";
import { useMemo, useRef, useState } from "react";
import {
  KioskButton,
  KioskDialog,
  KioskDialogContent,
  KioskDialogDescription,
  KioskDialogFooter,
  KioskDialogHeader,
  KioskDialogTitle,
  KioskDialogTrigger,
} from "../../elements";

interface RestartDialogProps {
  onRestart: () => void;
  onOpenSettings?: () => void;
  qrMode?: boolean;
}

/**
 * Restart Dialog
 *
 * Confirmation dialog for restarting the kiosk flow.
 * Positioned at top-left corner.
 */
export function RestartDialog({
  onRestart,
  onOpenSettings,
  qrMode,
}: RestartDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const clickTimestamps = useRef<number[]>([]);
  const t = useTranslations("Kiosk.MedriteCheckin.RestartDialog");
  const locale = useLocale();

  const handleRestart = () => {
    setIsOpen(false);
    onRestart();
  };

  const showEnglishText = useMemo(() => locale !== "en", [locale]);

  return (
    <KioskDialog open={isOpen} onOpenChange={setIsOpen}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="fixed top-4 left-4 z-[100] px-4 py-2  text-gray-700"
            title="Restart kiosk flow"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={-40}
          className="rounded-[20px] shadow-xl z-[100]"
        >
          <KioskDialogTrigger asChild>
            <button
              type="button"
              title="Restart kiosk flow"
              className="flex justify-between items-center w-full px-2 font-medium"
            >
              {t(KioskMedriteCheckinRestartDialogKeys.ExitToTheWelcomeScreen)}{" "}
              <span className="flex items-center gap-2">
                {showEnglishText && (
                  <span className=" text-gray-400">Exit</span>
                )}
                <LogOut className="h-4 w-4" />
              </span>
            </button>
          </KioskDialogTrigger>
          {onOpenSettings && !qrMode && (
            <button
              type="button"
              className="w-full flex justify-center pt-2 mt-2"
              onClick={() => {
                const now = Date.now();
                clickTimestamps.current = [
                  ...clickTimestamps.current.filter((ts) => now - ts < 500),
                  now,
                ];
                if (clickTimestamps.current.length >= 2) {
                  clickTimestamps.current = [];
                  onOpenSettings();
                }
              }}
            >
              <Settings className="h-5 w-5 text-gray-300" />
            </button>
          )}
        </PopoverContent>
      </Popover>

      <KioskDialogContent className="sm:max-w-md">
        <KioskDialogHeader>
          <KioskDialogTitle className="flex flex-col items-center justify-center !text-[24px]">
            {t(KioskMedriteCheckinRestartDialogKeys.DoYouWantToExit)}
            {showEnglishText && (
              <span className="text-gray-400 text-sm font-normal">
                Do you want to exit?
              </span>
            )}
          </KioskDialogTitle>
          <KioskDialogDescription className="flex flex-col items-center justify-center">
            {t(KioskMedriteCheckinRestartDialogKeys.YourAnswersWillNotBeSaved)}{" "}
            {showEnglishText && (
              <span className="text-gray-400 text-sm font-normal">
                Your answers will not be saved.
              </span>
            )}
          </KioskDialogDescription>
        </KioskDialogHeader>
        <KioskDialogFooter>
          <div className="w-full">
            <KioskButton
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              {t(KioskMedriteCheckinRestartDialogKeys.Cancel)}
            </KioskButton>
            {showEnglishText && (
              <p className="text-gray-400 text-sm font-normal w-full text-center">
                Cancel
              </p>
            )}
          </div>
          <div className="w-full">
            <KioskButton
              type="button"
              variant="primary"
              onClick={handleRestart}
            >
              {t(KioskMedriteCheckinRestartDialogKeys.Restart)}
            </KioskButton>
            {showEnglishText && (
              <p className="text-gray-400 text-sm font-normal w-full text-center">
                Restart
              </p>
            )}
          </div>
        </KioskDialogFooter>
      </KioskDialogContent>
    </KioskDialog>
  );
}
